import { AspectRatioKey, DesignChannel } from 'components/NewFileModal/Types'
import { AlmanacFile, AlmanacFileSummary, Archetype } from 'types'
import { CrossPostAccount } from '../Wizard/Form/CrossPostAccountsInput/CrossPostAccountsInput'
import { CaptionType, MultiStepSchema, WizardType } from '../schemas/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { Data } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'

export interface Values {
  width: number
  height: number
  initial_media_file_id: string
  initial_media_file_remove_background: boolean
  filename: string
  project: AlmanacFile
  source_visual_element_id: number
  brand_visual_kit_id?: number
  brand_voice_id?: number
  source_material_ids?: number[]
  apply_brand: boolean
  topic: string
  tone: Tone[]
  language?: string
  brand_kit_id?: number
  content?: object
  caption?: string
  cross_post_accounts?: CrossPostAccount[]
  audience?: string
  avoid?: string
  caption_type?: CaptionType
  include_emojis?: boolean
  links?: string[]
  post_length?: string
  archetype?: Archetype
}

export interface DesktopWizardProps {
  schema: MultiStepSchema<WizardType>
  initialValues?: {
    filename?: string
    project?: AlmanacFile
    language?: string
    brand_kit_id?: number
    source_visual_element_id?: number
    startWithTemplate?: boolean
    startFromBrandKit?: boolean
    initial_media_file_id?: string
    width?: number
    height?: number
    caption_type?: string
    topic?: string
  }
  initialStep?: number
  initialCompletedAll?: boolean
  isGeneratingDoc?: boolean
  onSuccess?: (data?: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> } | Data) => void
}

export enum FlowKey {
  A1 = 'A1',
  A2 = 'A2',
  B = 'B',
  C = 'C',
  D = 'D',
  F = 'F',
  E = 'E',
  G = 'G',
  H1 = 'H1',
  H2 = 'H2',
  I = 'I',
  J = 'J',
  K1 = 'K1',
  K2 = 'K2',
  L = 'L',
  M1 = 'M1',
  M2 = 'M2',
  M3 = 'M3',
  N = 'N',
  O = 'O',
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
  R = 'R',
  Z1 = 'Z1',
  Z2 = 'Z2',
}

export const FlowWizards: Record<AspectRatioKey, FlowKey> = {
  'landscape-post': FlowKey.C,
  'portrait-post': FlowKey.C,
  'square-post': FlowKey.C,
  'instagram-square-post': FlowKey.C,
  'instagram-portrait-post': FlowKey.C,
  'instagram-landscape-post': FlowKey.C,
  'instagram-story': FlowKey.A2,
  'facebook-story': FlowKey.A2,
  'google-my-business-post': FlowKey.C,
  'tik-tok-thumbnail': FlowKey.B,
  'youtube-channel-art': FlowKey.B,
  'youtube-profile-picture': FlowKey.B,
  'facebook-profile-picture': FlowKey.B,
  'twitter-profile-picture': FlowKey.B,
  'twitter-cover-image': FlowKey.B,
  'facebook-cover': FlowKey.B,
  'linkedin-profile-picture': FlowKey.B,
  'linkedin-banner': FlowKey.B,
  'whatsapp-sticker': FlowKey.B,
  'linkedin-post': FlowKey.C,
  'linkedin-portrait-post': FlowKey.C,
  'facebook-post': FlowKey.C,
  'facebook-portrait-post': FlowKey.C,
  'twitter-post': FlowKey.C,
  'twitter-portrait-post': FlowKey.C,
  'facebook-marketplace': FlowKey.F,
  pinterest: FlowKey.F,
  ebay: FlowKey.F,
  poshmark: FlowKey.F,
  depop: FlowKey.F,
  mercari: FlowKey.F,
  mercadolibre: FlowKey.F,
  shopee: FlowKey.F,
  lazada: FlowKey.F,
  etsy: FlowKey.F,
  vinted: FlowKey.F,
  amazon: FlowKey.F,
  'shopify-square': FlowKey.F,
  'instagram-reel': FlowKey.G,
  'tik-tok-post': FlowKey.G,
  'youtube-short': FlowKey.G,
}

// Maps a social design channel to its text only counterpart
export const DesignChannelToTextChannel: Partial<Record<DesignChannel, ChannelType>> = {
  [DesignChannel.InstagramPost]: ChannelType.InstagramPosts,
  [DesignChannel.FacebookPost]: ChannelType.FacebookPost,
  [DesignChannel.LinkedInPost]: ChannelType.LinkedInPosts,
  [DesignChannel.TwitterPost]: ChannelType.Tweets,
}

// Maps a text channel to its design channel counterpart
export const TextChannelToDesignChannel: Partial<Record<ChannelType, DesignChannel>> = {
  [ChannelType.InstagramPosts]: DesignChannel.InstagramPost,
  [ChannelType.FacebookPost]: DesignChannel.FacebookPost,
  [ChannelType.LinkedInPosts]: DesignChannel.LinkedInPost,
  [ChannelType.Tweets]: DesignChannel.TwitterPost,
}
